import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { AumBreadcrumbComponentItem, DateLocalePipe, IPageHeader } from '@modeso/aumico-lib-core-fe';
import { FCPActivationDetails, OrganizationProvider } from '@modeso/aumico-lib-users-fe';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ActionsSubject } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import { Observable, filter, map, tap } from 'rxjs';
import { RouteLabels } from '../../../enums/routeLabels.enum';

@UntilDestroy()
@Component({
    selector: 'app-enabling-closing-process',
    templateUrl: './enabling-closing-process.component.html',
    styleUrls: ['./enabling-closing-process.component.scss'],
})
export class EnablingClosingProcess implements OnInit {
    readonly breadcrumbItems: AumBreadcrumbComponentItem[] = [
        {
            displayName: RouteLabels.CLOSING_PROCESS_ENABLING,
        },
    ];
    readonly pageHeader: IPageHeader = {
        isLocalizedKey: true,
        value: RouteLabels.CLOSING_PROCESS_ENABLING,
    };

    fcpActivationDetails$: Observable<FCPActivationDetails | undefined>;
    organizationId: string;
    endDate: Date;
    showDeactivationForm: boolean = false;
    stopTrialForm: FormGroup;
    showActivateTrialModal: boolean;
    showCantDeactivateModal: boolean;

    constructor(
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private readonly matDialog: MatDialog,
        private readonly organizationprovider: OrganizationProvider,
        private readonly actionListener$: ActionsSubject,
        private i18n: I18NextService,
        private dateLocal: DateLocalePipe,
        private readonly fb: FormBuilder,
    ) {
        this.matIconRegistry.addSvgIcon(
            'aumico-pattern-icon-lock',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/aumico-pattern-icon-lock.svg`),
        );
    }

    ngOnInit(): void {
        this.fcpActivationDetails$ = this.organizationprovider.getCurrentOrganization$().pipe(
            filter((organization) => organization != null),
            tap((organization) => (this.organizationId = organization.id)),
            map((organization) => organization.fcpActivationDetails),
        );
    }

    activateClosingProcessTrial(): void {
        this.organizationprovider.dispatchAskForFCPTrial();
    }

    onConfirmActivateClosingProcessTrial() {
        this.showActivateTrialModal = false;
    }
    onCancelActivateClosingProcessTrial() {
        this.showActivateTrialModal = false;
    }
    deactivateClosingProcess(): void {
        this.showDeactivationForm = true;

        this.stopTrialForm = this.fb.group({
            survey: ['', Validators.required],
        });
    }
    confirmDeactivateClosingProcess(): void {
        if (this.stopTrialForm.invalid) {
            this.stopTrialForm.markAllAsTouched(); // Mark all controls as touched to trigger validation styles

            return;
        }
        this.showDeactivationForm = false;

        const { survey } = this.stopTrialForm.value;

        this.organizationprovider.dispatchDeactivateFCP(survey);
    }

    private showCantDeactivateDialog(): void {
        this.showCantDeactivateModal = true;
    }
    onConfirmCantActivateModal() {
        this.showCantDeactivateModal = false;
    }

    cancel() {
        this.stopTrialForm.reset();
    }

    get getDaysRemainingTrialText(): string {
        const endDate = new Date(this.endDate);
        const today = new Date();
        const timeDiff = endDate.getTime() - today.getTime();
        const x = Math.ceil(timeDiff / (1000 * 3600 * 24)); // Convert milliseconds to days
        const text = this.i18n.t('aumico_reports_fcp_trial_info_text', { x: x.toString() }) as string;
        return text;
    }
    get getDaysRemainingSubscriptionText(): string {
        const endDate = new Date(this.endDate);
        const today = new Date();
        const timeDiff = endDate.getTime() - today.getTime();
        const x = Math.ceil(timeDiff / (1000 * 3600 * 24)); // Convert milliseconds to days
        const text = this.i18n.t('aumico_reports_fcp_subscription_info_text', { x: x.toString() }) as string;
        return text;
    }
    get getEndDateText(): string {
        const endDate = new Date(this.endDate);
        const x = this.dateLocal.transform(endDate);
        const text = this.i18n.t('aumico_reports_fcp_deactivation_term_end_date', { date: x.toString() }) as string;
        return text;
    }
}
